import { AgentRunBadge as BaseAgentRunBadge } from '@reflex/ui/components/shared/AgentRunBadge';
import type { MentionDetailFact } from '@reflex/ui/components/shared/MentionRow';
import { timeAgo } from '@reflex/ui/lib/format';
import { useAgent } from '@/hooks/agents/useAgents';

export interface AgentRunBadgeProps {
  /** The referenced agent run id. */
  agentId: string;
  /** Fallback title shown before (or instead of) the live run name. */
  title: string;
  /** When provided, renders a remove affordance (composer chips). */
  onRemove?: () => void;
  className?: string;
}

/**
 * Live wrapper around the presentational {@link BaseAgentRunBadge}: fetches the
 * referenced run by id so the composer's badge popover shows up-to-date status,
 * start time, and type. Used where web data hooks are available (the launch
 * composer); the chat renders the presentational badge directly across the
 * plugin boundary.
 */
export function AgentRunBadge({ agentId, title, onRemove, className }: AgentRunBadgeProps) {
  const { data: agent } = useAgent(agentId);
  const name = agent?.name ?? title;
  const facts: MentionDetailFact[] = agent
    ? [
        { label: 'Status', value: agent.status.replace(/_/g, ' ') },
        { label: 'Started', value: timeAgo(agent.createdAt) },
        { label: 'Type', value: agent.agentType },
      ]
    : [];

  return (
    <BaseAgentRunBadge
      title={name}
      agentId={agentId}
      facts={facts}
      onRemove={onRemove}
      className={className}
    />
  );
}
